'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/Button';

/**
 * Animated 404 screen rendered by app/not-found.tsx for every unknown route.
 * Reuses the shared EnthiosHub Button + design tokens (no duplicate primitives).
 */
export function PageNotFound() {
  const router = useRouter();

  return (
    <main className="flex min-h-screen w-full items-center justify-center overflow-x-hidden bg-nature-bg px-4 py-10">
      <div className="mx-auto w-full max-w-2xl text-center">
        {/* Animated illustration with the 404 overlay */}
        <div
          role="img"
          aria-label="Animated illustration of a traveller who has lost their way"
          className="mx-auto flex h-56 w-full items-center justify-center bg-contain bg-center bg-no-repeat sm:h-80 md:h-96"
          style={{
            backgroundImage:
              "url('https://cdn.dribbble.com/users/285475/screenshots/2083086/dribbble_1.gif')",
          }}
        >
          <span className="text-7xl font-black tracking-tight text-nature-text drop-shadow-sm sm:text-8xl">
            404
          </span>
        </div>

        <div className="-mt-6 sm:-mt-10">
          <h1 className="text-2xl font-bold text-nature-text sm:text-3xl">
            Oops! Page Not Found
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-nature-muted sm:text-base">
            The page you&apos;re looking for may have been moved, deleted, or never existed.
          </p>

          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Home className="h-4 w-4" aria-hidden />}
              onClick={() => router.push('/')}
              className="w-full sm:w-auto"
            >
              Go to Home
            </Button>
            <Button
              variant="outline"
              size="lg"
              leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />}
              onClick={() => router.back()}
              className="w-full sm:w-auto"
            >
              Go Back
            </Button>
          </div>

          <p className="mt-7 text-sm text-nature-muted">
            Need help?{' '}
            <Link
              href="/help"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Visit our Support Center
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
